import { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'wouter';
import { analytics } from '@mindstudio-ai/interface';
import api from '../api';
import { useAuth, useToasts } from '../state';
import { withAdvisorPrep } from './AdvisorPrep';
import { ConfidentialityModal, needsConfidentialityAck } from './ConfidentialityModal';
import { useNotes, refreshNotes } from '../notes/store';
import { refreshIdeaWorkspace } from '../ideaScope/store';
import { captureSourceFor } from '../ideaScope/surfaces';
import { IDEA_FIELD_MAX } from './CharCount';

export type QuickAdvisor = 'market' | 'patent' | 'finance';
const NAME: Record<QuickAdvisor, string> = { market: 'Product Strategist', patent: 'IP Consultant', finance: 'Financial Analyst' };

/** A short title from the first sentence, the same rule the capture form uses for a pasted idea. */
/** Placeholder only: the server names the idea (autoTitle), keeping whole words. */
function titleFrom(text: string) {
  const first = text.trim().split(/[.\n]/)[0]?.trim() ?? '';
  return first.slice(0, 60) || 'Untitled idea';
}

/**
 * "+ New idea" on an advisor page: one box for the idea in plain words, then straight into that advisor's
 * session. No form and no "structure my idea" step; the advisor's session gathers the details. The full
 * capture form stays one link away.
 */
export type CaptureMode = 'idea' | 'note';

/**
 * "+ Capture" and N: one dialog with an Idea · Note switch. Idea mode is the quick "What's the idea?" box
 * (then straight into a session). Note mode jots a thought onto the current idea (or the inbox), keeps
 * the page it was written on, and closes with a toast so the member stays where they were.
 */
export function QuickStartIdea({
  advisor,
  open,
  onClose,
  initialMode = 'idea',
  ideaCtx = null,
  prefill = '',
  fromNoteId,
}: {
  advisor: QuickAdvisor;
  open: boolean;
  onClose: () => void;
  initialMode?: CaptureMode;
  /** The idea the member is inside, which a note defaults to. */
  ideaCtx?: { id: string; title: string } | null;
  /** Text to start with ("Make it an idea" from a note). */
  prefill?: string;
  /** The quick note this idea is made from: moved onto the new idea once it's saved. */
  fromNoteId?: string;
}) {
  const [mode, setMode] = useState<CaptureMode>(initialMode);
  const [location] = useLocation();
  const [noteText, setNoteText] = useState('');
  const [noteIdea, setNoteIdea] = useState<{ id: string; title: string } | null>(ideaCtx);
  const [picking, setPicking] = useState(false);
  const notesData = useNotes((s) => s.data);
  const loadNotes = useNotes((s) => s.load);
  const [, navigate] = useLocation();
  const profile = useAuth((s) => s.profile);
  const showToast = useToasts((s) => s.show);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ack, setAck] = useState(false);
  const ta = useRef<HTMLTextAreaElement>(null);
  const name = NAME[advisor];

  // Each open starts fresh from where the member is.
  useEffect(() => {
    if (!open) return;
    setMode(initialMode);
    setNoteIdea(ideaCtx);
    setPicking(false);
    setNoteText('');
    if (prefill) setText(prefill);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The idea's title can arrive after the dialog opened (its workspace was still loading).
  useEffect(() => {
    if (ideaCtx && noteIdea?.id === ideaCtx.id && noteIdea.title !== ideaCtx.title) setNoteIdea(ideaCtx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ideaCtx?.title]);

  useEffect(() => {
    if (!open) return;
    setError(null);
    window.setTimeout(() => ta.current?.focus(), 60);
    const key = (e: KeyboardEvent) => e.key === 'Escape' && !busy && onClose();
    window.addEventListener('keydown', key);
    return () => window.removeEventListener('keydown', key);
  }, [open, busy, onClose, mode]);

  if (!open) return null;

  const start = async () => {
    const idea = text.trim();
    if (idea.length < 8 || busy) {
      setError('Describe the idea in a sentence or two first.');
      return;
    }
    // First capture ever: the one-time confidentiality agreement comes first.
    if (profile && needsConfidentialityAck(profile)) {
      setAck(true);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await api.createIdea({ title: titleFrom(idea), problemStatement: idea, tagNames: [], autoTitle: true } as any);
      const id = (res.idea as any).id as string;
      analytics.track('quick_start_idea', { advisor });
      // Made from a quick note: the note moves onto its new idea.
      if (fromNoteId) {
        await api.updateNoteItem({ kind: 'note', id: fromNoteId, ideaId: id }).catch(() => null);
        refreshNotes();
      }
      if (advisor === 'market') return navigate(`/ideas/${id}/needs-discovery`);
      if (advisor === 'finance') return navigate(`/ideas/${id}/business-case`);
      await withAdvisorPrep('IP Consultant', () => api.startSession({ ideaId: id }));
      navigate(`/ideas/${id}/session`);
    } catch (e: any) {
      const msg = String(e?.message ?? '');
      setError(/You have used all/i.test(msg) ? `${msg} See plans for more.` : msg || 'Could not start the session. Try again.');
      if (!/You have used all/i.test(msg)) showToast('Could not start the session. Try again.', 'error');
      setBusy(false);
    }
  };

  const saveNote = async () => {
    const t = noteText.trim();
    if (!t || busy) {
      setError('Write the note first.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await api.saveQuickNote({
        text: t,
        ideaId: noteIdea?.id ?? null,
        sourceLabel: captureSourceFor(location + window.location.hash),
        sourcePath: location + window.location.search + window.location.hash,
      });
      analytics.track('quick_note_saved', { inIdea: !!noteIdea });
      const href = noteIdea ? `/ideas/${noteIdea.id}/notes` : '/notes';
      showToast('Note saved', 'success', { action: { label: 'View', onClick: () => navigate(href) } });
      if (noteIdea) void refreshIdeaWorkspace(noteIdea.id);
      refreshNotes();
      setBusy(false);
      onClose();
    } catch (e: any) {
      setError(String(e?.message ?? '') || 'Could not save the note. Try again.');
      setBusy(false);
    }
  };

  const openPicker = () => {
    setPicking(true);
    void loadNotes();
  };

  const switcher = (
    <div className="qs__mode" role="tablist" aria-label="What are you capturing?">
      {(['idea', 'note'] as const).map((m) => (
        <button
          key={m}
          type="button"
          role="tab"
          aria-selected={mode === m}
          className={`qs__mode-b${mode === m ? ' is-on' : ''}`}
          disabled={busy}
          onClick={() => {
            setMode(m);
            setError(null);
            window.setTimeout(() => ta.current?.focus(), 30);
          }}
        >
          {m === 'idea' ? 'Idea' : 'Note'}
        </button>
      ))}
    </div>
  );

  if (mode === 'note') {
    return (
      <>
        <style>{QS_CSS}</style>
        <div className="qs-scrim" onClick={() => !busy && onClose()} aria-hidden="true" />
        <div className="qs" role="dialog" aria-modal="true" aria-labelledby="qs-h">
          <button type="button" className="qs__x" aria-label="Close" disabled={busy} onClick={onClose}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
          {switcher}
          <h2 className="qs__h" id="qs-h">
            Jot it down
          </h2>
          <div className="qs__for">
            {picking ? (
              <select
                className="qs__pick"
                autoFocus
                value={noteIdea?.id ?? ''}
                aria-label="Which idea is this note for?"
                onChange={(e) => {
                  const id = e.target.value;
                  const found = notesData?.ideas.find((i) => i.id === id) ?? (ideaCtx?.id === id ? ideaCtx : null);
                  setNoteIdea(id && found ? { id: found.id, title: found.title } : null);
                  setPicking(false);
                }}
                onBlur={() => setPicking(false)}
              >
                <option value="">No idea yet (Notes inbox)</option>
                {ideaCtx && !notesData?.ideas.some((i) => i.id === ideaCtx.id) && <option value={ideaCtx.id}>{ideaCtx.title}</option>}
                {(notesData?.ideas ?? []).map((i) => (
                  <option key={i.id} value={i.id}>
                    {i.title}
                  </option>
                ))}
              </select>
            ) : (
              <>
                <span>{noteIdea ? <>for <b>{noteIdea.title}</b></> : <>Goes to your <b>Notes inbox</b></>}</span>
                <button type="button" className="qs__chg" onClick={openPicker} disabled={busy}>
                  change
                </button>
              </>
            )}
          </div>
          <textarea
            ref={ta}
            className="qs__ta"
            value={noteText}
            onChange={(e) => {
              setNoteText(e.target.value);
              if (error) setError(null);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void saveNote();
            }}
            placeholder="A question to check, a part to rethink, a name for it..."
            rows={4}
            maxLength={2000}
            disabled={busy}
            aria-label="Your note"
          />
          <div className="qs__note">Private to you. {noteText.length > 1800 ? `${2000 - noteText.length} characters left.` : 'Cmd/Ctrl + Enter saves.'}</div>
          <div className="qs__err" role="alert">
            {error}
          </div>
          <div className="qs__acts">
            <button type="button" className="qs__go qs__go--note" onClick={() => void saveNote()} disabled={busy}>
              {busy ? <span className="qs__ring" aria-hidden="true" /> : <>Save note</>}
            </button>
            <Link href={noteIdea ? `/ideas/${noteIdea.id}/notes` : '/notes'} className="qs__alt" onClick={onClose}>
              See all notes
            </Link>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <style>{QS_CSS}</style>
      <div className="qs-scrim" onClick={() => !busy && onClose()} aria-hidden="true" />
      <div className="qs" role="dialog" aria-modal="true" aria-labelledby="qs-h">
        <button type="button" className="qs__x" aria-label="Close" disabled={busy} onClick={onClose}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </button>
        {switcher}
        <div className="qs__eb">New idea · {name}</div>
        <h2 className="qs__h" id="qs-h">
          What&rsquo;s the idea?
        </h2>
        <p className="qs__p">A sentence or two in plain words is enough. Your {name} asks for the rest during the session.</p>
        <textarea
          ref={ta}
          className="qs__ta"
          value={text}
          onChange={(e) => {
            setText(e.target.value);
            if (error) setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) void start();
          }}
          placeholder="e.g. A pannier mount that clips onto any bike rack in one move, without tools."
          rows={4}
          maxLength={IDEA_FIELD_MAX}
          disabled={busy}
          aria-label="Describe your idea"
        />
        <div className="qs__note">
          {text.length >= IDEA_FIELD_MAX * 0.9
            ? `${(IDEA_FIELD_MAX - text.length).toLocaleString()} characters left.`
            : 'Private and encrypted. Never shared, never used to train AI.'}
        </div>
        <div className="qs__err" role="alert">
          {error}
          {error && /plans/i.test(error) && (
            <>
              {' '}
              <Link href="/plans">View plans</Link>
            </>
          )}
        </div>
        <div className="qs__acts">
          <button type="button" className="qs__go" onClick={() => void start()} disabled={busy}>
            {busy ? <span className="qs__ring" aria-hidden="true" /> : <>Start my {name} session &rarr;</>}
          </button>
          <Link href={`/ideas/new?then=${advisor}`} className="qs__alt" onClick={onClose}>
            Use the full form instead
          </Link>
        </div>
      </div>
      <ConfidentialityModal
        open={ack}
        onAccept={() => {
          setAck(false);
          void start();
        }}
        onClose={() => setAck(false)}
      />
    </>
  );
}

const QS_CSS = `
.qs-scrim { position: fixed; inset: 0; z-index: 1400; background: rgba(15,23,20,.32); animation: qsFade .18s ease-out; }
.qs { position: fixed; z-index: 1401; left: 50%; top: 50%; transform: translate(-50%, -50%); width: min(560px, calc(100vw - 32px));
  background: #fffdf8; border-radius: 16px; padding: 28px; box-shadow: 0 0 0 1px rgba(15,23,20,.07), 0 24px 60px rgba(15,23,20,.22); animation: qsIn .22s cubic-bezier(.32,.72,0,1); }
@keyframes qsFade { from { opacity: 0; } }
@keyframes qsIn { from { opacity: 0; transform: translate(-50%, -46%); } }
.qs__x { position: absolute; top: 14px; right: 14px; width: 36px; height: 36px; display: grid; place-items: center; border: 0; border-radius: 999px;
  background: transparent; color: rgba(15,23,20,.55); cursor: pointer; transition: background .14s ease, color .14s ease; }
.qs__x:hover:not(:disabled) { background: rgba(15,23,20,.06); color: var(--ink); }
.qs__x:disabled { opacity: .4; cursor: default; }
.qs__mode { display: inline-flex; padding: 3px; gap: 2px; border-radius: 999px; background: rgba(15,23,20,.06); margin-bottom: 16px; user-select: none; }
.qs__mode-b { height: 30px; min-width: 72px; padding: 0 14px; border: 0; border-radius: 999px; background: transparent; color: rgba(15,23,20,.62);
  font: 600 13px var(--f-sans); cursor: pointer; transition: background .16s ease, color .16s ease, box-shadow .16s ease; }
.qs__mode-b.is-on { background: #fff; color: var(--ink); box-shadow: 0 1px 2px rgba(15,23,20,.12), 0 0 0 1px rgba(15,23,20,.06); }
.qs__mode-b:focus-visible { outline: 2px solid var(--brass); outline-offset: 2px; }
.qs__for { display: flex; align-items: center; gap: 10px; min-height: 34px; margin: 0 0 12px; font-size: 14.5px; color: rgba(15,23,20,.62); }
.qs__for b { color: var(--ink); font-weight: 600; }
.qs__chg { border: 0; background: none; padding: 0; font: 500 13.5px var(--f-sans); color: var(--brass-deep); text-decoration: underline; text-underline-offset: 3px; cursor: pointer; }
.qs__pick { height: 34px; max-width: 100%; padding: 0 10px; border: 0; border-radius: 8px; background: #fff; box-shadow: inset 0 0 0 1px rgba(15,23,20,.18);
  font: 500 14px var(--f-sans); color: var(--ink); }
.qs__go--note { min-width: 160px; }
.qs__eb { font: 600 10.5px var(--f-mono); letter-spacing: .12em; text-transform: uppercase; color: var(--brass-deep); }
.qs__h { font: 400 28px/1.15 var(--f-display, var(--f-serif)); margin: 8px 0 6px; color: var(--ink); }
.qs__p { font-size: 14.5px; line-height: 1.5; color: rgba(15,23,20,.62); margin: 0 0 16px; }
.qs__ta { width: 100%; box-sizing: border-box; resize: vertical; min-height: 110px; padding: 14px 16px; border: 0; border-radius: 12px; background: #fff;
  box-shadow: inset 0 0 0 1px rgba(15,23,20,.14); font: 400 17px/1.5 var(--f-serif); color: var(--ink); outline: none; }
.qs__ta:focus { box-shadow: inset 0 0 0 1px rgba(15,23,20,.14), 0 0 0 3px rgba(181,133,60,.35); }
.qs__note { font: 500 10.5px var(--f-mono); color: rgba(15,23,20,.5); margin-top: 8px; }
.qs__err { min-height: 20px; margin-top: 6px; font-size: 13px; color: var(--oxblood); }
.qs__err a { color: var(--heartwood); font-weight: 600; }
.qs__acts { display: flex; align-items: center; gap: 16px; flex-wrap: wrap; margin-top: 6px; }
.qs__go { height: 46px; min-width: 260px; padding: 0 22px; border: 0; border-radius: 999px; background: var(--heartwood); color: var(--parchment);
  font: 600 15px var(--f-sans); cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.qs__go:hover:not(:disabled) { background: color-mix(in oklab, var(--heartwood) 88%, white); }
.qs__go:disabled { cursor: default; opacity: .85; }
.qs__go:focus-visible { outline: 2px solid var(--brass); outline-offset: 2px; }
.qs__ring { width: 16px; height: 16px; border-radius: 50%; border: 2px solid currentColor; border-top-color: transparent; animation: qsSpin .8s linear infinite; }
@keyframes qsSpin { to { transform: rotate(1turn); } }
.qs__alt { font-size: 13.5px; color: rgba(15,23,20,.62); text-decoration: underline; text-underline-offset: 3px; }
@media (max-width: 600px) {
  .qs { top: auto; bottom: 0; left: 0; transform: none; width: 100%; border-radius: 20px 20px 0 0; padding: 22px 18px calc(18px + env(safe-area-inset-bottom)); animation: qsUp .26s cubic-bezier(.32,.72,0,1); }
  @keyframes qsUp { from { transform: translateY(100%); } }
  .qs__go { width: 100%; min-width: 0; }
  .qs__for { flex-wrap: wrap; }
}
@media (prefers-reduced-motion: reduce) { .qs, .qs-scrim { animation: none; } }
`;
